import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mui/material";
import { IoChevronBack, IoChevronForward, IoClose } from "react-icons/io5";

interface Category {
  id: string;
  image: string;
  image_selected?: string;
  categoryTitle: string;
  description?: string;
}

interface SelectedCategoriesPanelProps {
  selectedCategories: string[];
  categories: Category[];
  onRemoveCategory: (categoryId: string) => void;
  currentLanguage: string;
}

const SelectedCategoriesPanel = ({
  selectedCategories,
  categories,
  onRemoveCategory,
  currentLanguage,
}: SelectedCategoriesPanelProps) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery("(min-width: 768px)"); // md breakpoint

  // Panel visibility state - Initially closed (only arrow visible). On desktop, panel is always open.
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [isPanelHovered, setIsPanelHovered] = useState(false);
  const panelHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-hide panel after delay - only on mobile when panel is open
  useEffect(() => {
    if (selectedCategories.length === 0) {
      setIsPanelVisible(false);
      return;
    }

    // On desktop: panel stays open, no auto-hide
    if (isDesktop) return;

    // On mobile: auto-hide if panel is visible and not hovered
    if (isPanelVisible && !isPanelHovered) {
      if (panelHideTimeoutRef.current) {
        clearTimeout(panelHideTimeoutRef.current);
      }
      panelHideTimeoutRef.current = setTimeout(() => {
        setIsPanelVisible(false);
      }, 3000);
    }

    return () => {
      if (panelHideTimeoutRef.current) {
        clearTimeout(panelHideTimeoutRef.current);
      }
    };
  }, [selectedCategories.length, isPanelHovered, isPanelVisible, isDesktop]);

  // Handle panel hover
  const handlePanelMouseEnter = () => {
    setIsPanelHovered(true);
    if (panelHideTimeoutRef.current) {
      clearTimeout(panelHideTimeoutRef.current);
    }
  };

  const handlePanelMouseLeave = () => {
    setIsPanelHovered(false);
    // Only auto-hide on mobile
    if (!isDesktop && selectedCategories.length > 0) {
      panelHideTimeoutRef.current = setTimeout(() => {
        setIsPanelVisible(false);
      }, 3000);
    }
  };

  // Handle arrow hover/click to toggle panel (on desktop: no toggle, panel stays open)
  const handleArrowClick = () => {
    if (isDesktop) return; // Desktop: panel cannot be closed
    setIsPanelVisible(!isPanelVisible);
    setIsPanelHovered(true);
    if (panelHideTimeoutRef.current) {
      clearTimeout(panelHideTimeoutRef.current);
    }
  };

  // On desktop: panel always open when categories exist. On mobile: use toggle state.
  const effectivePanelVisible = isDesktop ? selectedCategories.length > 0 : isPanelVisible;

  // Get selected category details
  const getSelectedCategoryDetails = () => {
    return selectedCategories
      .map((id) => categories.find((cat) => cat.id === id))
      .filter((cat) => cat !== undefined) as Category[];
  };

  if (selectedCategories.length === 0) {
    return null;
  }

  return (
    <>
      {/* Panel with attached arrow - Slider effect */}
      <div
        className={`fixed end-0 top-1/2 -translate-y-1/2 z-50 transition-all duration-300 flex items-center ${effectivePanelVisible ? "translate-x-0" : "translate-x-[calc(100%-48px)] md:translate-x-[calc(100%-48px)] lg:translate-x-[calc(100%-3.125vw)] rtl:translate-x-[calc(-100%+48px)] md:rtl:translate-x-[calc(-100%+48px)] lg:rtl:translate-x-[calc(-100%+3.125vw)]"
          }`}
        onMouseEnter={handlePanelMouseEnter}
        onMouseLeave={handlePanelMouseLeave}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        {/* Arrow Button - Attached to panel */}
        <button
          onClick={handleArrowClick}
          className="2xl:w-[3.125vw] w-6 sm:w-10 md:w-12 2xl:h-[6.25vw] h-14 sm:h-20 md:h-24 bg-primary-yellow 2xl:rounded-s-[0.52vw] rounded-s-xl rtl:scale-x-100 2xl:border-[0.104vw] border-[1.5px] border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] flex items-center justify-center cursor-pointer hover:bg-primary-orange transition-all duration-200 group flex-shrink-0 z-10  md:hidden"
          aria-label={effectivePanelVisible ? (isDesktop ? "Panel" : "Close panel") : "Open panel"}
        >
          {effectivePanelVisible ? (
            <IoChevronForward
              className={`2xl:w-[1.25vw] w-4 sm:w-4 md:w-5 2xl:h-[1.25vw] h-4 sm:h-4 md:h-5 text-black-300 group-hover:scale-110 transition-transform duration-200 ${currentLanguage === "ar" ? "rotate-180" : ""
                }`}
            />
          ) : (
            <IoChevronBack
              className={`2xl:w-[1.25vw] w-4 sm:w-4 md:w-5 2xl:h-[1.25vw] h-4 sm:h-4 md:h-5 text-black-300 group-hover:scale-110 transition-transform duration-200 ${currentLanguage === "ar" ? "rotate-180" : ""
                }`}
            />
          )}
        </button>

        {/* Panel Content */}
        <div
          className={`bg-white 2xl:rounded-s-[0.833vw] rounded-s-2xl rtl:rounded-e-none  2xl:border-[0.156vw] border-2 border-black-300 2xl:shadow-[0px_0.208vw_0px_#110706] shadow-[0px_3px_0px_#110706]  flex flex-col 2xl:max-h-[70vh] max-h-[75vh] transition-all duration-300  ${effectivePanelVisible ? "w-fit opacity-100 pointer-events-auto" : "w-0 opacity-0 overflow-hidden pointer-events-none "
            } 2xl:p-[0.533vw] p-0.5 sm:p-1.5 md:p-1`}
        >
          {/* 2xl:w-[7vw] w-18 sm:w-24 md:w-24 */}
          {/* Header */}
          {/* <div className="flex items-center justify-between 2xl:mb-[0.833vw] mb-2 sm:mb-2.5 md:mb-3 2xl:pb-[0.625vw] pb-1.5 sm:pb-2 md:pb-2 border-b-2 border-black-300 shrink-0">
            <h3 className="2xl:text-[1.042vw]/tight text-xs/tight sm:text-sm/tight md:text-base/tight lg:text-lg/tight font-bold text-black-300">
              {t("createGame.selectedCategories")}
            </h3>
            <div className="flex items-center gap-2">
              <span className="2xl:text-[0.938vw]/tight text-xs/tight sm:text-sm/tight md:text-base/tight font-bold text-primary-orange 2xl:px-[0.625vw] px-1.5 sm:px-2 md:px-2.5 2xl:py-[0.156vw] py-0.5 bg-primary-yellow 2xl:rounded-[0.26vw] rounded-md 2xl:border-[0.104vw] border-[1.5px] border-black-300">
                {selectedCategories.length}
              </span>

            </div>
          </div> */}

          {/* Category List - Scrollable, expands vertically */}
          <div className="flex flex-col 2xl:gap-[0.625vw] gap-1.5 sm:gap-2 overflow-y-auto  flex-1 min-h-0 hide-scroll">
            {getSelectedCategoryDetails().map((category) => (
              <div
                key={category.id}
                className="relative 2xl:h-[5.2vw] h-16 sm:h-20 md:h-18 lg:h-18 xl:h-18 2xl:rounded-[0.52vw] rounded-lg 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-white 2xl:shadow-[0px_0.104vw_0px_#110706] shadow-[0px_2px_0px_#110706] overflow-hidden group transition-all duration-200 shrink-0 w-fit"
              >
                <img
                  src={category.image_selected || category.image}
                  alt={category.categoryTitle}
                  className="h-full object-contain"
                />
                {/* Cross Icon - Remove Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveCategory(category.id);
                  }}
                  className={`absolute top-0 2xl:w-[1.2vw] w-4 2xl:h-[1.2vw] h-4 flex items-center justify-center bg-primary-orange 2xl:rounded-[0.26vw] rounded-md 2xl:border-[0.104vw] border-[1.5px] border-black-300 2xl:shadow-[0px_0.104vw_0px_#110706] shadow-[0px_1px_0px_#110706] hover:bg-primary-orange/90 transition-all duration-200 z-10 ${currentLanguage === "ar" ? "start-0 2xl:rounded-tr-[0.26vw] rounded-tr-md 2xl:rounded-bl-none rounded-bl-none" : "end-0 2xl:rounded-tl-[0.26vw] rounded-tl-md 2xl:rounded-br-none rounded-br-none"
                    }`}
                  aria-label="Remove category"
                >
                  <IoClose className="2xl:w-[0.833vw] w-3 2xl:h-[0.833vw] h-3 text-white" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default SelectedCategoriesPanel;
